<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import Cookie from 'js-cookie';
import ModalAddCity from '../documents/Modals/Modal_Add_city.vue';
import { useMainStore } from '@/stores/index';
import defaultAvatar from '@/assets/img/default-avatar.svg';

const store = useMainStore();
store.Token();

const ModalAddCityRef = ref(null);
const showNotifyMenu = ref(false);
const notifyDropdownRef = ref(null);

const limitedNotifyMessages = computed(() => {
  if (!Array.isArray(store.notifyMessages)) return [];
  return store.notifyMessages.slice(0, 5);
});

const hasNotifications = computed(() => {
  return Array.isArray(store.notifyMessages) && store.notifyMessages.length > 0;
});

const totalNotifications = computed(() => {
  return Array.isArray(store.notifyMessages) ? store.notifyMessages.length : 0;
});

const logout = () => {
  Cookie.remove('User');
  Cookie.remove('Session_ID');
  location.reload();
};

const registerHall = () => {
  ModalAddCityRef.value?.openModal();
};

const toggleSidebar = () => {
  store.isPinned = !store.isPinned;
};

const truncateText = (text, length = 20) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const getNotifyRoute = (notify) => {
  if (notify.tosigner !== undefined || notify.tosignerAttach !== undefined) {
    return 'Assinaturas';
  }
  return 'Recebidos';
};

const onAvatarError = (event) => {
  event.target.src = defaultAvatar;
};

const handleClickOutside = (event) => {
  if (notifyDropdownRef.value && !notifyDropdownRef.value.contains(event.target)) {
    showNotifyMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="navbar-container">
    <ModalAddCity ref="ModalAddCityRef" />

    <nav
      id="navbarBlur"
      class="navbar navbar-main navbar-expand-lg nav-background h-100"
    >
      <div class="container-fluid px-3 py-1 d-flex align-items-center justify-content-between h-100">
        <router-link class="navbar-brand d-flex align-items-center m-0" to="/">
          <img
            v-if="!store.mobile"
            src="../../assets/img/Logo_2docs_extend.png"
            class="navbar-brand-img"
            alt="Smart2Doc Logo"
          />
          <img
            v-else
            src="../../assets/img/2D.png"
            class="navbar-brand-img"
            alt="Smart2Doc Logo"
            style="width: 36px; height: auto;"
          />
        </router-link>

        <ul class="navbar-nav d-flex flex-row align-items-center ms-auto mb-0 list-unstyled">
          <li v-if="store.role === 1" class="nav-item me-3 d-none d-md-block">
            <button
              type="button"
              class="btn btn-sm mb-0 text-white shadow-none d-flex align-items-center"
              style="background-color: #1733d3; border-radius: 6px; padding: 6px 14px;"
              @click="registerHall"
            >
              <i class="fa fa-plus-circle me-2" aria-hidden="true"></i>
              Cadastrar prefeitura
            </button>
          </li>

          <li ref="notifyDropdownRef" class="nav-item position-relative me-3">
            <button
              type="button"
              class="btn-icon-nav"
              @click.stop="showNotifyMenu = !showNotifyMenu"
              title="Notificações"
              :aria-expanded="showNotifyMenu"
            >
              <font-awesome-icon
                v-if="hasNotifications"
                :icon="['fas', 'bell']"
                style="color: #FFC107;"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'bell']"
                style="color: #FFFFFF;"
              />
            </button>

            <transition name="dropdown-fade">
              <div v-if="showNotifyMenu" class="dropdown-notify-menu shadow-lg">
                <div class="px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                  <span class="font-docs font-weight-bold text-dark" style="font-size: 15px;">Notificações</span>
                  <span v-if="hasNotifications" class="badge bg-warning text-dark font-docs" style="font-size: 11px;">
                    {{ totalNotifications }}
                  </span>
                </div>

                <div class="notify-list-container">
                  <template v-if="hasNotifications">
                    <router-link
                      v-for="notify in limitedNotifyMessages"
                      :key="notify.id || notify.protocol || notify"
                      :to="{ name: getNotifyRoute(notify) }"
                      class="notify-item d-flex align-items-center px-3 py-2 text-decoration-none"
                      @click="showNotifyMenu = false"
                    >
                      <img
                        :src="$userAvatar(notify?.author?.user?.path_image)"
                        @error="onAvatarError"
                        alt="Avatar"
                        class="notify-avatar me-2"
                      />
                      <div class="d-flex flex-column text-truncate" style="min-width: 0;">
                        <span class="font-docs font-weight-bold text-dark text-truncate" style="font-size: 13px;">
                          {{ truncateText(notify.protocol, 22) }}
                        </span>
                        <span class="font-docs text-muted text-truncate" style="font-size: 12px;">
                          {{ truncateText(notify.subject, 24) }}
                        </span>
                        <span
                          v-if="notify.tosigner !== undefined"
                          class="font-docs font-weight-600"
                          style="color: rgb(153, 116, 4); font-size: 11px;"
                        >
                          Assinatura necessária
                        </span>
                        <span
                          v-else-if="notify.tosignerAttach !== undefined"
                          class="font-docs font-weight-600"
                          style="color: rgb(153, 116, 4); font-size: 11px;"
                        >
                          Assinatura anexo(os)
                        </span>
                      </div>
                    </router-link>

                    <div v-if="totalNotifications >= 6" class="p-2 text-center border-top">
                      <router-link
                        :to="{ name: 'Recebidos' }"
                        class="font-docs Color-Docs font-weight-bold text-decoration-none"
                        style="font-size: 13px;"
                        @click="showNotifyMenu = false"
                      >
                        Ver mais +
                      </router-link>
                    </div>
                  </template>

                  <div v-else class="p-4 text-center">
                    <img
                      src="../../assets/img/check.png"
                      class="avatar avatar-sm mb-2"
                      alt="Sem notificações"
                      style="width: 32px; height: 32px;"
                    />
                    <p class="font-docs text-muted mb-0" style="font-size: 13px;">
                      Nada por aqui ainda!
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </li>

          <li class="nav-divider" aria-hidden="true"></li>

          <li class="nav-item">
            <router-link :to="{ name: 'profile' }" class="nav-user-link d-flex align-items-center text-decoration-none">
              <img
                :src="$userAvatar(store.user?.path_image || store.user?.profile_image)"
                @error="onAvatarError"
                alt="Foto de perfil"
                class="nav-user-avatar"
              />
              <span class="nav-user-name ms-2 font-docs text-white">
                <b>{{ store.user?.name }}</b>
              </span>
            </router-link>
          </li>

          <li class="nav-divider d-none d-sm-block" aria-hidden="true"></li>

          <li class="nav-item d-none d-sm-flex align-items-center">
            <button
              type="button"
              class="btn-icon-nav"
              @click="logout"
              title="Sair"
            >
              <img src="../../assets/img/out.png" alt="Sair" class="logout-icon" />
            </button>
          </li>

          <li class="nav-item d-flex d-sm-none align-items-center ms-2">
            <button
              type="button"
              class="btn-icon-nav"
              @click="toggleSidebar"
              title="Menu de navegação"
            >
              <i class="fa fa-bars text-white" style="font-size: 18px;" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<style scoped>
.navbar-container {
  width: 100%;
}

.btn-icon-nav {
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  padding: 4px;
  font-size: 17px;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.btn-icon-nav:hover {
  opacity: 0.85;
}

.btn-icon-nav:active {
  transform: scale(0.95);
}

.nav-divider {
  width: 1px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.4);
  margin: 0 12px;
}

.nav-user-link {
  transition: opacity 0.15s ease;
}

.nav-user-link:hover {
  opacity: 0.9;
}

.nav-user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
}

.nav-user-name {
  font-size: 15px;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .nav-user-name {
    max-width: 110px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .nav-user-name {
    display: none;
  }
}

.logout-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  transition: transform 0.15s ease;
}

.logout-icon:hover {
  transform: translateX(2px);
}

.dropdown-notify-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: -8px;
  width: 280px;
  background-color: #FFFFFF;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 1050;
  overflow: hidden;
}

.notify-list-container {
  max-height: 320px;
  overflow-y: auto;
}

.notify-item {
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #F5F5F5;
}

.notify-item:hover {
  background-color: #F8F9FA;
}

.notify-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
